import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { StyledButton } from '@app/components/button/Button';
import { TextField } from '@app/components/form-fields/TextField';
import { SelectField } from '@app/components/form-fields/SelectField';
import type {
    BusinessHours,
    DayHours,
    DayKey,
    Location,
    LocationConfigItem,
} from '@app/services/api.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Flex, Tabs, TimePicker, type ModalProps, type TabsProps } from 'antd';
import type { MouseEvent } from 'react';
import { useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import {
    Controller,
    // useFieldArray,
    useForm,
} from 'react-hook-form';
import { MdCheck } from 'react-icons/md';
import * as z from 'zod';
import useBrands from '@app/hooks/brands/useBrands';
import { countryOptions } from '@app/utils/constants';
import { startCase } from 'lodash';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FormField } from '@app/components/form-fields/FormField';
import { StyledTooltip } from '@app/components/tooltip/Tooltip';
import { IoHelpCircle } from 'react-icons/io5';

dayjs.extend(customParseFormat);

/** The seven day keys every \`business_hours\` object must carry. */
export const BUSINESS_HOURS_DAYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
] as const satisfies readonly DayKey[];

const format = 'HH:mm';

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** A whole number of 0 or more (blank counts as 0), optionally capped at \`max\`. */
const durationPartSchema = (label: string, max?: number) =>
    z
        .string()
        .trim()
        .regex(/^\d*$/, `${label} must be a whole number of 0 or more`)
        .refine((value) => max === undefined || Number(value) <= max, {
            message: `${label} must be ${max} or less`,
        });

const timeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Time must be in HH:MM format')
    .nullable();

/**
 * A day row is valid when both times are set (close after open) or neither is set.
 * An empty row is transformed to `null`.
 */
const dayHoursSchema = z
    .object({ open: timeSchema, close: timeSchema })
    .superRefine(({ open, close }, ctx) => {
        if (open && !close) {
            ctx.addIssue({ code: 'custom', path: ['close'], message: 'Close time is required' });
        }
        if (!open && close) {
            ctx.addIssue({ code: 'custom', path: ['open'], message: 'Open time is required' });
        }
        // HH:MM strings compare correctly lexicographically
        if (open && close && close <= open) {
            ctx.addIssue({
                code: 'custom',
                path: ['close'],
                message: 'Close time must be after open time',
            });
        }
    })
    .transform(({ open, close }): DayHours | null => (open && close ? { open, close } : null));

const schema = z.object({
    name: z.string().trim().min(1, 'Location name is required'),
    brand_id: z.string().min(1, 'Brand is required'),

    // Contact
    contact_name: z.string().trim().min(1, 'Admin name is required'),
    contact_email: z
        .string()
        .email('Admin email must be valid')
        .trim()
        .min(1, 'Admin email is required'),
    contact_telephone: z.string().trim().min(1, 'Admin phone is required'),

    // Address
    address_line1: z.string().trim().min(1, 'Address Line 1 is required'),
    address_line2: z.string().trim(),
    city: z.string().trim().min(1, 'City is required'),
    post_code: z.string().trim().min(1, 'Post code is required'),
    country: z.string().trim().min(1, 'Country is required'),

    // Business Details
    // Entered as days / hours / minutes, sent to the API as total minutes.
    // Blank boxes count as 0 — the API doesn't accept null.
    minimum_booking_notice: z
        .object({
            days: durationPartSchema('Days'),
            hours: durationPartSchema('Hours', 23),
            minutes: durationPartSchema('Minutes', 59),
        })
        .transform(
            ({ days, hours, minutes }): number =>
                Number(days) * MINUTES_PER_DAY + Number(hours) * MINUTES_PER_HOUR + Number(minutes)
        ),

    // Business Hours
    business_hours: z.record(z.enum(BUSINESS_HOURS_DAYS), dayHoursSchema),
});

/** Shape of the form state while editing (times may be partially filled). */
type LocationFormInput = z.input<typeof schema>;

/** Shape passed to `onSubmit` once validated (`business_hours` is `BusinessHours`). */
export type LocationFormValues = z.output<typeof schema>;

/** Settings sent to the API as \`{ field, value }\` entries in the \`config\` array. */
export type LocationConfig =
    | { field: 'business_hours'; value: BusinessHours }
    | { field: 'minimum_booking_notice'; value: number };

/** Payload passed to \`onSubmit\`: business details are moved into \`config\`. */
export type LocationFormPayload = Omit<
    LocationFormValues,
    'business_hours' | 'minimum_booking_notice'
> & {
    channel_id: string;
    config: LocationConfig[];
};

export type LocationFormProps = ModalProps & {
    id: string;
    /** Pass the single-location response to prefill business details from \`config\`. */
    data?: Location & { config?: LocationConfigItem[] };
    isEdit?: boolean;
    onSubmit: (data: LocationFormPayload, reset: UseFormReset<LocationFormInput>) => void;
    /**
     * Optional handler invoked when user clicks "Test Connection".
     * Receives current form values and may return a Promise<boolean> or void.
     */
    onTestConnection?: (values: LocationFormValues) => Promise<boolean> | void;
};

const DEFAULT_BUSINESS_HOURS = Object.fromEntries(
    BUSINESS_HOURS_DAYS.map((day) => [day, { open: null, close: null }])
) as LocationFormInput['business_hours'];

/** Pre-filled hours for new locations: every day 09:00–17:30. */
const ADD_DEFAULT_BUSINESS_HOURS = Object.fromEntries(
    BUSINESS_HOURS_DAYS.map((day) => [day, { open: '09:00', close: '17:30' }])
) as LocationFormInput['business_hours'];

/** Splits total minutes into the days / hours / minutes boxes. */
const minutesToDuration = (total: number): LocationFormInput['minimum_booking_notice'] => ({
    days: String(Math.floor(total / MINUTES_PER_DAY)),
    hours: String(Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR)),
    minutes: String(total % MINUTES_PER_HOUR),
});

/** 24 hours — shown as 1 day, 0 hours, 0 mins. */
const DEFAULT_MINIMUM_BOOKING_NOTICE = minutesToDuration(MINUTES_PER_DAY);

/** Reads one config entry's value; the caller states the expected type. */
const getConfigValue = <T,>(config: LocationConfigItem[] | undefined, field: string) =>
    config?.find((item) => item.field === field)?.value as T | undefined;

/** Maps saved hours into the form, dropping extra keys (e.g. \`exceptions\`) and filling missing days. */
const toBusinessHoursInput = (saved: Partial<BusinessHours>): LocationFormInput['business_hours'] =>
    Object.fromEntries(
        BUSINESS_HOURS_DAYS.map((day) => [
            day,
            { open: saved[day]?.open ?? null, close: saved[day]?.close ?? null },
        ])
    ) as LocationFormInput['business_hours'];

const DEFAULT_VALUES = {
    name: '',
    brand_id: undefined,
    contact_name: '',
    contact_email: '',
    contact_telephone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    post_code: '',
    country: 'GB',
    minimum_booking_notice: DEFAULT_MINIMUM_BOOKING_NOTICE,
    business_hours: DEFAULT_BUSINESS_HOURS,
    // crmCredentials: [],
    // postBookingsTo: 'crm',
};

export const LocationForm = ({
    onCancel,
    onSubmit,
    data,
    isEdit = false,
    //   onTestConnection,
    ...props
}: LocationFormProps) => {
    const {
        handleSubmit,
        control,
        reset,
        // getValues,
        formState: { isValid, isSubmitting },
    } = useForm<LocationFormInput, unknown, LocationFormValues>({
        mode: 'all',
        resolver: zodResolver(schema),
        defaultValues: isEdit
            ? DEFAULT_VALUES
            : { ...DEFAULT_VALUES, business_hours: ADD_DEFAULT_BUSINESS_HOURS },
    });

    const { data: brandsData } = useBrands({ per_page: 10000 });

    useEffect(() => {
        // reset with incoming data if present, otherwise keep defaults
        if (data) {
            const savedHours = getConfigValue<Partial<BusinessHours>>(
                data.config,
                'business_hours'
            );
            const savedNotice = getConfigValue<number>(data.config, 'minimum_booking_notice');
            reset({
                // id: data.id ?? '',
                name: data.name ?? '',
                brand_id: data.brand_id ?? undefined,
                contact_name: data.contact_name ?? '',
                contact_email: data.contact_email ?? '',
                contact_telephone: data.contact_telephone ?? '',
                address_line1: data.address_line1 ?? '',
                address_line2: data.address_line2 ?? '',
                city: data.city ?? '',
                post_code: data.post_code ?? '',
                country: data.country ?? 'GB',
                // Business details come from `config`; fall back to defaults when not saved
                minimum_booking_notice:
                    typeof savedNotice === 'number'
                        ? minutesToDuration(savedNotice)
                        : DEFAULT_MINIMUM_BOOKING_NOTICE,
                business_hours: savedHours
                    ? toBusinessHoursInput(savedHours)
                    : DEFAULT_BUSINESS_HOURS,
            });
        }
    }, [data, reset]);

    const handlePreSubmit = (values: LocationFormValues) => {
        const { business_hours, minimum_booking_notice, ...rest } = values;
        const channelId = brandsData?.data?.find((b) => b.id === values.brand_id)?.channel_id || '';
        onSubmit(
            {
                ...rest,
                channel_id: channelId,
                config: [
                    { field: 'business_hours', value: business_hours },
                    { field: 'minimum_booking_notice', value: minimum_booking_notice },
                ],
            },
            reset
        );
    };

    const tabs: TabsProps['items'] = [
        {
            key: 'location-details',
            label: 'Location Details',
            children: (
                <div className="form-fields-wrapper">
                    <div className="grid">
                        <div className="grid-xs-12 grid-s-12 grid-md-12">
                            <Controller
                                control={control}
                                name="name"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Location Name"
                                        type="text"
                                        placeholder="Enter Location Name"
                                    />
                                )}
                            />
                        </div>
                        <div className="grid-xs-12 grid-s-12 grid-md-12">
                            <Controller
                                control={control}
                                name="brand_id"
                                render={({ field, fieldState }) => (
                                    <SelectField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Brand"
                                        placeholder="Select Brand"
                                        options={
                                            brandsData?.data?.map((brand) => ({
                                                label: brand.name,
                                                value: brand.id,
                                            })) ?? []
                                        }
                                    />
                                )}
                            />
                        </div>

                        {/* Contact */}
                        <div className="grid-xs-12 grid-s-6 grid-md-6">
                            <Controller
                                control={control}
                                name="contact_name"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Contact Name"
                                        type="text"
                                        placeholder="Enter contact name"
                                    />
                                )}
                            />
                        </div>

                        <div className="grid-xs-12 grid-s-6 grid-md-6">
                            <Controller
                                control={control}
                                name="contact_email"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        disabled={isEdit}
                                        label="Contact Email"
                                        type="email"
                                        placeholder="Enter contact email"
                                    />
                                )}
                            />
                        </div>

                        <div className="grid-xs-12 grid-s-12 grid-md-12">
                            <Controller
                                control={control}
                                name="contact_telephone"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Contact Phone"
                                        type="tel"
                                        placeholder="Enter contact phone"
                                    />
                                )}
                            />
                        </div>

                        {/* Address */}
                        <div className="grid-xs-12 grid-s-12 grid-md-12">
                            <Controller
                                control={control}
                                name="address_line1"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Address Line 1"
                                        type="text"
                                        placeholder="Enter address line 1"
                                    />
                                )}
                            />
                        </div>

                        <div className="grid-xs-12 grid-s-12 grid-md-12">
                            <Controller
                                control={control}
                                name="address_line2"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Address Line 2"
                                        type="text"
                                        placeholder="Enter address line 2 (optional)"
                                    />
                                )}
                            />
                        </div>

                        <div className="grid-xs-12 grid-s-12 grid-md-6">
                            <Controller
                                control={control}
                                name="city"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        label="City"
                                        type="text"
                                        placeholder="Enter city"
                                    />
                                )}
                            />
                        </div>

                        <div className="grid-xs-12 grid-s-12 grid-md-6">
                            <Controller
                                control={control}
                                name="post_code"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Post Code"
                                        type="text"
                                        placeholder="Enter post code"
                                    />
                                )}
                            />
                        </div>

                        <div className="grid-xs-12 grid-s-12 grid-md-12">
                            <Controller
                                control={control}
                                name="country"
                                render={({ field, fieldState }) => (
                                    <SelectField
                                        fieldState={fieldState}
                                        {...field}
                                        label="Country"
                                        placeholder="Select Country"
                                        options={countryOptions}
                                    />
                                )}
                            />
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: 'business-details',
            label: 'Business Details',
            children: (
                <div className="form-fields-wrapper">
                    <div className="grid">
                        <div className="grid-xs-12 form-field" style={{ marginBottom: -20 }}>
                            <div className="label">
                                Minimum Booking Notice{' '}
                                <StyledTooltip title="The shortest amount of time ahead that someone can book. Slots sooner than this won't be offered.">
                                    <IoHelpCircle
                                        size={18}
                                        color="black"
                                        style={{ verticalAlign: 'middle' }}
                                    />
                                </StyledTooltip>
                            </div>
                        </div>
                        <div className="grid-xs-12 grid-s-4 grid-md-4">
                            <Controller
                                control={control}
                                name="minimum_booking_notice.days"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        aria-label="Minimum Booking Notice Days"
                                        type="number"
                                        min={0}
                                        step={1}
                                        placeholder="0"
                                        suffix="days"
                                    />
                                )}
                            />
                        </div>
                        <div className="grid-xs-12 grid-s-4 grid-md-4">
                            <Controller
                                control={control}
                                name="minimum_booking_notice.hours"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        aria-label="Minimum Booking Notice Hours"
                                        type="number"
                                        min={0}
                                        max={23}
                                        step={1}
                                        placeholder="0"
                                        suffix="hours"
                                    />
                                )}
                            />
                        </div>
                        <div className="grid-xs-12 grid-s-4 grid-md-4">
                            <Controller
                                control={control}
                                name="minimum_booking_notice.minutes"
                                render={({ field, fieldState }) => (
                                    <TextField
                                        fieldState={fieldState}
                                        {...field}
                                        aria-label="Minimum Booking Notice Minutes"
                                        type="number"
                                        min={0}
                                        max={59}
                                        step={1}
                                        placeholder="0"
                                        suffix="mins"
                                    />
                                )}
                            />
                        </div>

                        <div className="grid-xs-12 form-field">
                            <div className="label">
                                Business Hours{' '}
                                <StyledTooltip title="The location's opening hours for each day. Set both an open and a close time, or leave both empty to mark the day as closed.">
                                    <IoHelpCircle
                                        size={18}
                                        color="black"
                                        style={{ verticalAlign: 'middle' }}
                                    />
                                </StyledTooltip>
                            </div>
                            <Flex vertical gap={10}>
                                {BUSINESS_HOURS_DAYS.map((day) => (
                                    <div
                                        key={day}
                                        className="grid business-hours-config"
                                        style={{ rowGap: 5 }}>
                                        <div
                                            className="grid-xs-12 grid-s-4 label"
                                            style={{ fontSize: 14 }}>
                                            {startCase(day)}
                                        </div>
                                        <div className="grid-xs-6 grid-s-4">
                                            <Controller
                                                control={control}
                                                name={`business_hours.${day}.open`}
                                                rules={{ deps: `business_hours.${day}.close` }}
                                                render={({ field, fieldState }) => (
                                                    <FormField error={fieldState?.error?.message}>
                                                        <TimePicker
                                                            minuteStep={5}
                                                            format={format}
                                                            placeholder="Open Time"
                                                            ref={field.ref}
                                                            name={field.name}
                                                            value={
                                                                field.value
                                                                    ? dayjs(field.value, format)
                                                                    : null
                                                            }
                                                            onChange={(time) =>
                                                                field.onChange(
                                                                    time
                                                                        ? time.format(format)
                                                                        : null
                                                                )
                                                            }
                                                            onBlur={field.onBlur}
                                                            status={
                                                                fieldState.error
                                                                    ? 'error'
                                                                    : undefined
                                                            }
                                                            aria-label={`${startCase(
                                                                day
                                                            )} Open Time`}
                                                        />
                                                    </FormField>
                                                )}
                                            />
                                        </div>
                                        <div className="grid-xs-6 grid-s-4">
                                            <Controller
                                                control={control}
                                                name={`business_hours.${day}.close`}
                                                rules={{ deps: `business_hours.${day}.open` }}
                                                render={({ field, fieldState }) => (
                                                    <FormField error={fieldState?.error?.message}>
                                                        <TimePicker
                                                            minuteStep={5}
                                                            format={format}
                                                            placeholder="Close Time"
                                                            ref={field.ref}
                                                            name={field.name}
                                                            value={
                                                                field.value
                                                                    ? dayjs(field.value, format)
                                                                    : null
                                                            }
                                                            onChange={(time) =>
                                                                field.onChange(
                                                                    time
                                                                        ? time.format(format)
                                                                        : null
                                                                )
                                                            }
                                                            onBlur={field.onBlur}
                                                            status={
                                                                fieldState.error
                                                                    ? 'error'
                                                                    : undefined
                                                            }
                                                            aria-label={`${startCase(
                                                                day
                                                            )} Close Time`}
                                                        />
                                                    </FormField>
                                                )}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </Flex>
                        </div>

                        {/* <div className="grid-xs-12">
                            <h2 className="h5" style={{ marginBottom: 20 }}>
                                Business Hours Exceptions
                            </h2>
                            <Flex vertical gap={10}>
                                <div
                                    className="business-exceptions-config"
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: '32px 1fr 1fr 1fr 80px',
                                        rowGap: 5,
                                        columnGap: 20,
                                    }}>
                                    <div>
                                        <StyledTooltip title="Remove this row">
                                            <StyledButton
                                                htmlType="button"
                                                // size="small"
                                                type="text"
                                                color="red"
                                                icon={<IoIosRemoveCircle size={25} />}
                                                iconOnly
                                            />
                                        </StyledTooltip>
                                    </div>
                                    <div>
                                        <DatePicker />
                                    </div>
                                    <div>
                                        <TimePicker
                                            minuteStep={5}
                                            format={format}
                                            placeholder="Open Time"
                                        />
                                    </div>
                                    <div>
                                        <TimePicker
                                            minuteStep={5}
                                            format={format}
                                            placeholder="Close Time"
                                        />
                                    </div>
                                    <div>
                                        <StyledTooltip title="Mark the Business as 'Open' or 'Closed' on this specific date.">
                                            <Switch
                                                checkedChildren="Opened"
                                                unCheckedChildren="Closed"
                                                style={{ marginBlock: 7 }}
                                                styles={{
                                                    content: {
                                                        fontSize: 12,
                                                    },
                                                }}
                                            />
                                        </StyledTooltip>
                                    </div>
                                </div>
                                <div>
                                    <p>No exceptions for this location.</p>
                                </div>
                                <div style={{ marginTop: 10 }}>
                                    <StyledButton size="small" icon={<IoIosAdd />}>
                                        Add new exception
                                    </StyledButton>
                                </div>
                            </Flex>
                        </div> */}
                    </div>
                </div>
            ),
        },
    ];

    return (
        <form onSubmit={handleSubmit((values) => handlePreSubmit(values))} id={props.id}>
            <Tabs defaultActiveKey="location-details" items={tabs} className="sticky-tabs" />

            <div className="ant-modal-footer">
                <StyledButtonGroup justify="flex-end">
                    <StyledButton
                        onClick={(e) => onCancel?.(e as MouseEvent<HTMLButtonElement>)}
                        color="black"
                        data-testid="reset-button">
                        Cancel
                    </StyledButton>
                    <StyledButton
                        htmlType="submit"
                        type="primary"
                        disabled={!isValid}
                        loading={isSubmitting || props?.loading}
                        icon={<MdCheck />}
                        data-testid="register-Partner-button">
                        Save
                    </StyledButton>
                </StyledButtonGroup>
            </div>
        </form>
    );
};
